import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../services/supabase';
import southShop from '../assets/southShop.png';

function SignUp() {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const { signUp } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      // Sign up the user with Supabase Auth
      const { user: authUser, error: signUpError } = await signUp(email, password);
      if (signUpError) throw signUpError;

      // Create a record in the public.users table
      const { error: insertError } = await supabase
        .from('users')
        .insert({
          email: email,
          first_name: firstName,
          last_name: lastName,
          password_hash: authUser?.password_hash || null, // This might not be available depending on your auth setup
          role: 'worker', // Default role as per your schema
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });
      if (insertError) throw insertError;

      navigate('/login'); // Redirect to login after signup
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden">
      {/* Background Image with Gradient Overlay */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url(${southShop})`, // Use southShop as the background image
        }}
      >
        {/* Gradient Overlay */}
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0) 25%, rgba(243, 244, 246, 1) 100%)',
          }}
        />
      </div>

      {/* 45-Degree Diagonal Section */}
      <div
        className="absolute inset-0 hidden md:block"
        style={{
          background: 'rgba(99, 102, 241, 0.1)', // Indigo with opacity
          clipPath: 'polygon(0 0, 100% 0, 0 100%)', // Creates a 45-degree diagonal from top-left to bottom-right
        }}
      />

      {/* SignUp Form */}
      <div className="relative bg-white p-8 rounded-2xl shadow-2xl w-full max-w-md transform transition-all duration-300 hover:shadow-3xl">
        <h2 className="text-3xl font-extrabold mb-6 text-center text-gray-900 tracking-tight">
          Create Your Account
        </h2>
        {error && (
          <p className="text-red-500 mb-4 text-center bg-red-50 py-2 rounded-lg">
            {error}
          </p>
        )}
        <form onSubmit={handleSubmit}>
          <div className="mb-5">
            <label
              className="block text-gray-700 mb-2 text-sm font-medium"
              htmlFor="first-name"
            >
              First Name
            </label>
            <input
              type="text"
              id="first-name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 shadow-sm hover:shadow-md"
              placeholder="Enter your first name"
              required
            />
          </div>
          <div className="mb-5">
            <label
              className="block text-gray-700 mb-2 text-sm font-medium"
              htmlFor="last-name"
            >
              Last Name
            </label>
            <input
              type="text"
              id="last-name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 shadow-sm hover:shadow-md"
              placeholder="Enter your last name"
              required
            />
          </div>
          <div className="mb-5">
            <label
              className="block text-gray-700 mb-2 text-sm font-medium"
              htmlFor="email"
            >
              Email Address
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 shadow-sm hover:shadow-md"
              placeholder="Enter your email"
              required
            />
          </div>
          <div className="mb-5">
            <label
              className="block text-gray-700 mb-2 text-sm font-medium"
              htmlFor="password"
            >
              Password
            </label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 shadow-sm hover:shadow-md"
              placeholder="Enter your password"
              required
            />
          </div>
          <div className="mb-6">
            <label
              className="block text-gray-700 mb-2 text-sm font-medium"
              htmlFor="confirm-password"
            >
              Confirm Password
            </label>
            <input
              type="password"
              id="confirm-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 shadow-sm hover:shadow-md"
              placeholder="Confirm your password"
              required
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className={classNames(
              'w-full p-3 rounded text-white transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5',
              loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700',
            )}
          >
            {loading ? 'Signing Up...' : 'Sign Up'}
          </button>
        </form>
        <p className="mt-6 text-center text-gray-600 text-sm">
          Already have an account?{' '}
          <Link
            to="/login"
            className="text-indigo-600 hover:text-indigo-800 font-medium transition duration-200"
          >
            Log in
          </Link>
        </p>
      </div>
    </div>
  );
}

// Simple utility function for classNames
function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default SignUp;